import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Search,
  HelpCircle,
  Book,
  MessageCircle,
  Phone,
  Mail,
  FileText,
  Video,
  Users,
  Shield,
  Upload,
  BarChart3,
  Settings,
  ExternalLink,
} from "lucide-react"
import bgImage from "@/assets/favicon.png"

const faqData = [
  {
    category: "General",
    questions: [
      {
        question: "How do I upload a new document?",
        answer:
          "To upload a document, go to the Dashboard and click on the file upload area. You can drag and drop files or click 'Choose Files' to select documents. Supported formats include PDF, DOC, DOCX, JPG, and PNG files up to 10MB.",
      },
      {
        question: "How do I create a new user account?",
        answer:
          "Navigate to the Users page and click 'Add User'. Fill in the required information including name, email, role, department, and badge number. The new user will receive login credentials via email.",
      },
      {
        question: "Can I edit document information after upload?",
        answer:
          "Yes, after a document is processed, you can view and edit the extracted information in the popup form. Click on any document in the Documents list and select 'View' to see the details.",
      },
    ],
  },
  {
    category: "Documents",
    questions: [
      {
        question: "What file formats are supported for upload?",
        answer:
          "The system supports PDF, DOC, DOCX, JPG, and PNG files. Maximum file size is 10MB per document. For best OCR results, use high-quality scanned documents or clear photographs.",
      },
      {
        question: "How does the OCR processing work?",
        answer:
          "After uploading a document, the system automatically performs OCR (Optical Character Recognition) to extract text, then uses AI to structure the information into a General Diary format. This process typically takes 30-60 seconds.",
      },
      {
        question: "Can I delete uploaded documents?",
        answer:
          "Yes, you can delete documents from the Documents page. Click the actions menu (three dots) next to any document and select 'Delete'. This action cannot be undone.",
      },
    ],
  },
  {
    category: "Reports",
    questions: [
      {
        question: "How do I generate a crime statistics report?",
        answer:
          "Go to the Reports page, select 'Crime Statistics' from the report type dropdown, choose your date range and police station filter, then click 'Generate Report'. The report will include charts and detailed breakdowns.",
      },
      {
        question: "Can I export reports to PDF?",
        answer:
          "Yes, after generating a report, you can export it to PDF by clicking the 'Export PDF' button in the report view. The exported file will include all charts and data tables.",
      },
      {
        question: "How often should I generate reports?",
        answer:
          "Report frequency depends on your role and requirements. Monthly reports are recommended for crime statistics, while officer performance reports can be generated quarterly.",
      },
    ],
  },
  {
    category: "User Management",
    questions: [
      {
        question: "How do I reset a user's password?",
        answer:
          "From the Users page, click on a user's actions menu and select 'Reset Password'. The user will receive a password reset link via email. For security, only administrators can reset passwords.",
      },
      {
        question: "What are the different user roles?",
        answer:
          "The system supports various roles including Inspector, Sub-Inspector, Assistant Sub-Inspector, Head Constable, and Constable. Each role has different permissions for viewing and editing data.",
      },
      {
        question: "How do I deactivate a user account?",
        answer:
          "In the Users page, edit the user's profile and change their status to 'Inactive'. Inactive users cannot log in but their data is preserved for record-keeping purposes.",
      },
    ],
  },
]

const tutorialData = [
  {
    title: "Getting Started with the Dashboard",
    description: "Learn the basics of navigating the police management system",
    duration: "5 min",
    category: "Basics",
    icon: BarChart3,
  },
  {
    title: "Document Upload and Processing",
    description: "Step-by-step guide to uploading and processing police documents",
    duration: "8 min",
    category: "Documents",
    icon: Upload,
  },
  {
    title: "User Management",
    description: "How to create, edit, and manage user accounts",
    duration: "6 min",
    category: "Administration",
    icon: Users,
  },
  {
    title: "Generating Reports",
    description: "Create and export various types of police reports",
    duration: "10 min",
    category: "Reports",
    icon: FileText,
  },
  {
    title: "Security Best Practices",
    description: "Maintain security and privacy in the system",
    duration: "7 min",
    category: "Security",
    icon: Shield,
  },
  {
    title: "System Settings",
    description: "Customize your profile and application preferences",
    duration: "4 min",
    category: "Settings",
    icon: Settings,
  },
]

export default function Help() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [supportForm, setSupportForm] = useState({
    subject: "",
    category: "",
    priority: "",
    message: "",
  })

  const categories = ["all", "General", "Documents", "Reports", "User Management"]

  const filteredFAQ = faqData.filter((category) => {
    if (selectedCategory === "all") return true
    return category.category === selectedCategory
  })

  const filteredQuestions = filteredFAQ.flatMap((category) =>
    category.questions.filter(
      (q) =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  )

  const handleSupportSubmit = () => {
    console.log("Support request submitted:", supportForm)
    // Reset form
    setSupportForm({ subject: "", category: "", priority: "", message: "" })
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-black">Help & Support</h1>
          <p className="text-gray-600">Find answers to your questions and get help with the system</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4 text-center">
              <HelpCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-black">
                {faqData.reduce((acc, cat) => acc + cat.questions.length, 0)}
              </div>
              <p className="text-sm text-gray-600">FAQ Articles</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4 text-center">
              <Video className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-black">{tutorialData.length}</div>
              <p className="text-sm text-gray-600">Video Tutorials</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4 text-center">
              <Book className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-black">12</div>
              <p className="text-sm text-gray-600">User Guides</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4 text-center">
              <MessageCircle className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-black">24/7</div>
              <p className="text-sm text-gray-600">Support Available</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100">
            <TabsTrigger value="faq" className="data-[state=active]:bg-white">
              FAQ
            </TabsTrigger>
            <TabsTrigger value="tutorials" className="data-[state=active]:bg-white">
              Tutorials
            </TabsTrigger>
            <TabsTrigger value="guides" className="data-[state=active]:bg-white">
              User Guides
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-white">
              Contact Support
            </TabsTrigger>
          </TabsList>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search FAQ..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-gray-300"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {categories.map((category) => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                        className={
                          selectedCategory === category ? "bg-black text-white" : "border-gray-300 hover:bg-gray-50"
                        }
                      >
                        {category === "all" ? "All" : category}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* FAQ Accordion */}
                <Accordion type="single" collapsible className="space-y-2">
                  {searchTerm
                    ? filteredQuestions.map((q, index) => (
                        <AccordionItem
                          key={index}
                          value={`item-${index}`}
                          className="border border-gray-200 rounded-lg px-4"
                        >
                          <AccordionTrigger className="text-left hover:no-underline">
                            <span className="text-black font-medium">{q.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600 pb-4">{q.answer}</AccordionContent>
                        </AccordionItem>
                      ))
                    : filteredFAQ.map((category) => (
                        <div key={category.category} className="space-y-2">
                          <h3 className="text-lg font-semibold text-black mt-6 mb-3">{category.category}</h3>
                          {category.questions.map((q, qIndex) => (
                            <AccordionItem
                              key={qIndex}
                              value={`${category.category}-${qIndex}`}
                              className="border border-gray-200 rounded-lg px-4"
                            >
                              <AccordionTrigger className="text-left hover:no-underline">
                                <span className="text-black font-medium">{q.question}</span>
                              </AccordionTrigger>
                              <AccordionContent className="text-gray-600 pb-4">{q.answer}</AccordionContent>
                            </AccordionItem>
                          ))}
                        </div>
                      ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tutorials Tab */}
          <TabsContent value="tutorials">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Video Tutorials
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tutorialData.map((tutorial, index) => (
                    <Card
                      key={index}
                      className="border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <tutorial.icon className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-black mb-1">{tutorial.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">{tutorial.description}</p>
                            <div className="flex items-center justify-between">
                              <Badge className="bg-gray-100 text-gray-800">{tutorial.category}</Badge>
                              <span className="text-xs text-gray-500">{tutorial.duration}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Guides Tab */}
          <TabsContent value="guides">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Book className="h-5 w-5" />
                  User Guides & Documentation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    {
                      title: "System Overview",
                      description: "Complete guide to the police management system features and capabilities",
                      type: "PDF Guide",
                    },
                    {
                      title: "Document Processing Workflow",
                      description: "Detailed workflow for document upload, OCR processing, and form generation",
                      type: "Step-by-step Guide",
                    },
                    {
                      title: "User Role Permissions",
                      description: "Understanding different user roles and their access permissions",
                      type: "Reference Guide",
                    },
                    {
                      title: "Report Generation Manual",
                      description: "How to create, customize, and export various types of reports",
                      type: "User Manual",
                    },
                    {
                      title: "Security Guidelines",
                      description: "Best practices for maintaining security and data privacy",
                      type: "Security Guide",
                    },
                    {
                      title: "Troubleshooting Common Issues",
                      description: "Solutions to frequently encountered problems and error messages",
                      type: "Troubleshooting",
                    },
                  ].map((guide, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-600" />
                        <div>
                          <h3 className="font-medium text-black">{guide.title}</h3>
                          <p className="text-sm text-gray-600">{guide.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-blue-100 text-blue-800">{guide.type}</Badge>
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Support Tab */}
          <TabsContent value="contact">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-black">Phone Support</p>
                      <p className="text-sm text-gray-600">+91 1800-XXX-XXXX</p>
                      <p className="text-xs text-gray-500">Available 24/7</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-black">Email Support</p>
                      <p className="text-sm text-gray-600">support@policeportal.gov.in</p>
                      <p className="text-xs text-gray-500">Response within 4 hours</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-black">Live Chat</p>
                      <p className="text-sm text-gray-600">Available on this page</p>
                      <p className="text-xs text-gray-500">Mon-Fri, 9 AM - 6 PM</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Support Request Form */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black">Submit Support Request</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">Subject</Label>
                    <Input
                      value={supportForm.subject}
                      onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                      placeholder="Brief description of your issue"
                      className="border-gray-300"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-black">Category</Label>
                      <select
                        value={supportForm.category}
                        onChange={(e) => setSupportForm({ ...supportForm, category: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select category</option>
                        <option value="technical">Technical Issue</option>
                        <option value="account">Account Problem</option>
                        <option value="feature">Feature Request</option>
                        <option value="bug">Bug Report</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-black">Priority</Label>
                      <select
                        value={supportForm.priority}
                        onChange={(e) => setSupportForm({ ...supportForm, priority: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">Message</Label>
                    <Textarea
                      value={supportForm.message}
                      onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                      placeholder="Describe your issue in detail..."
                      className="border-gray-300"
                      rows={4}
                    />
                  </div>
                  <Button onClick={handleSupportSubmit} className="w-full bg-black text-white hover:bg-gray-800">
                    Submit Request
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
